import {
  AppCard,
  Icon,
  PageContainer,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import {
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useListServicePurchaseOrders } from "../../../../__generated__/orval/endpoints/purchase-orders/purchase-orders";
import type { EntitiesPurchaseOrderServiceQuotationCollectionListResponseOutput } from "../../../../__generated__/orval/models/entitiesPurchaseOrderServiceQuotationCollectionListResponse.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { ServicePurchaseOrdersFilters } from "./-internal_components/ServicePurchaseOrdersFilters/index";

const searchSchema = z.object({
  assigned_to: z.string().optional().catch(undefined),
  item_count: z.coerce.number().int().positive().optional().catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
  purchase_group_id: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined),
  purchase_order_number: z.optional(z.string()).catch(undefined),
  rowsPerPage: z.coerce.number().int().min(1).default(25).catch(25),
  sap_created_at: z.optional(z.string()).catch(undefined),
  vendor: z.optional(z.string()).catch(undefined),
});

const ServicePurchaseOrders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const assignedToIds = search.assigned_to
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  const rowsPerPage = search.rowsPerPage || 25;
  const listParams = {
    assigned_to: assignedToIds?.length ? assignedToIds.join(",") : undefined,
    item_count: search.item_count,
    items_per_page: rowsPerPage,
    page: search.page || 1,
    purchase_group_id: search.purchase_group_id,
    purchase_order_number: search.purchase_order_number || undefined,
    sap_created_at: search.sap_created_at || undefined,
    vendor: search.vendor || undefined,
  };
  const {
    data: response,
    isError,
    isLoading,
  } = useListServicePurchaseOrders<EntitiesPurchaseOrderServiceQuotationCollectionListResponseOutput>(
    listParams,
  );

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("servicePurchaseOrders.fetchError"));
    }
  }, [isError, t]);

  return (
    <PageContainer
      maxWidth={false}
      showBackButton={false}
      title={t("servicePurchaseOrders.title")}
    >
      <AppCard>
        <ServicePurchaseOrdersFilters loading={isLoading} />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress aria-label={t("common.loading")} />
          </Box>
        ) : isError ? (
          <Typography
            color="error"
            role="alert"
            sx={{ py: 4, textAlign: "center" }}
          >
            {t("servicePurchaseOrders.fetchError")}
          </Typography>
        ) : response?.results.length ? (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table
                aria-label={t("servicePurchaseOrders.title")}
                size="small"
                sx={{ minWidth: 1024 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("servicePurchaseOrders.purchaseOrderNumber")}
                    </TableCell>
                    <TableCell>{t("common.vendor")}</TableCell>
                    <TableCell>{t("common.responsible")}</TableCell>
                    <TableCell>{t("common.purchaseGroup")}</TableCell>
                    <TableCell>
                      {t("servicePurchaseOrders.itemCount")}
                    </TableCell>
                    <TableCell>{t("common.createdAt")}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {response.results.map((purchaseOrder) => (
                    <TableRow hover key={purchaseOrder.id}>
                      <TableCell>
                        <Typography component="span" variant="body2">
                          {purchaseOrder.po_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{purchaseOrder.vendor_name}</TableCell>
                      <TableCell>
                        {purchaseOrder.responsible_name &&
                        purchaseOrder.responsible_email
                          ? `${purchaseOrder.responsible_name} - ${purchaseOrder.responsible_email}`
                          : purchaseOrder.responsible_name ||
                            purchaseOrder.responsible_email}
                      </TableCell>
                      <TableCell>
                        {purchaseOrder.purchase_group || "-"}
                      </TableCell>
                      <TableCell>{purchaseOrder.item_count ?? "-"}</TableCell>
                      <TableCell>
                        {t(
                          "intlDate",
                          i18nextParseToDate(
                            purchaseOrder.sap_created_at ??
                              purchaseOrder.created_at,
                          ),
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title={t("servicePurchaseOrders.moreDetails")}>
                          <RouteIconButtonLink
                            aria-label={t("servicePurchaseOrders.moreDetails")}
                            params={{
                              purchaseOrderId: String(purchaseOrder.id),
                            }}
                            to="/purchase_orders/service_quotations/$purchaseOrderId"
                          >
                            <Icon icon={mdiEye} />
                          </RouteIconButtonLink>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={Math.max(1, response.pagination.total_pages)}
                onChange={(_event, page) => {
                  navigate({ search: (previous) => ({ ...previous, page }) });
                }}
                page={search.page}
              />
            </Box>
          </>
        ) : (
          <Typography
            color="text.secondary"
            sx={{ py: 4, textAlign: "center" }}
          >
            {t("servicePurchaseOrders.emptyState")}
          </Typography>
        )}
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/purchase_orders/service_quotations/",
  // biome-ignore assist/source/useSortedKeys: ordering required by type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Purchase Order - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ServicePurchaseOrders,
});
